/**
 * Live School du dimanche — lot à CHOISIR (migration 0331) : un élève du
 * podium qui possédait déjà la SKILLCARD mise en jeu choisit celle qu'il veut
 * parmi les cartes publiées qu'il n'a pas (celles du programme du live d'abord).
 */

import { publishProgress } from '@/lib/progress-sync';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { CardColorTheme, CardRarity } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export interface LivePrizeChoice {
  challenge_id: string;
  rank: number;
  title: string | null;
  level: string;
  starts_at: string;
  prize_card_id: string;
  prize_title: string | null;
}

export interface LivePrizeChoiceCard {
  id: string;
  title: string;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  domain_label: string | null;
  levels: string[];
  in_programme: boolean;
}

export function livePrizeChoiceErrorMessage(code: string): string {
  switch (code) {
    case 'already_chosen':
      return 'Tu as déjà choisi ta SKILLCARD pour ce live.';
    case 'already_owned':
      return 'Tu possèdes déjà cette SKILLCARD : choisis-en une autre.';
    case 'card_unavailable':
      return 'Cette SKILLCARD n’est plus disponible.';
    case 'not_found':
      return 'Aucun lot à choisir pour ce live.';
    case 'account_required':
      return 'Connecte-toi avec ton compte pour choisir ta SKILLCARD.';
    default:
      return 'Le choix n’a pas pu être enregistré. Réessaie.';
  }
}

/** « 1re place », « 2e place »… */
export function prizePlaceLabel(rank: number): string {
  return rank === 1 ? '1re place' : `${rank}e place`;
}

export const myLivePrizeChoicesKey = (userId: string) => ['live-prize-choices', userId] as const;

export function useMyLivePrizeChoices(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: myLivePrizeChoicesKey(userId ?? ''),
    enabled: !!userId && enabled,
    staleTime: 60_000,
    queryFn: async (): Promise<LivePrizeChoice[]> => {
      const { data, error } = await db.rpc('get_my_live_prize_choices');
      if (error) throw new Error(error.message);
      return (data ?? []) as LivePrizeChoice[];
    },
  });
}

export function useLivePrizeChoiceCards(challengeId: string) {
  return useQuery({
    queryKey: ['live-prize-choice-cards', challengeId],
    queryFn: async (): Promise<LivePrizeChoiceCard[] | null> => {
      const { data, error } = await db.rpc('live_prize_choice_cards', {
        p_challenge_id: challengeId,
      });
      if (error) throw new Error(error.message);
      const r = data as { cards?: LivePrizeChoiceCard[]; error?: string };
      return r?.error ? null : (r?.cards ?? []);
    },
  });
}

export function useChooseLivePrize(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { challengeId: string; cardId: string }) => {
      const { data, error } = await db.rpc('choose_live_prize', {
        p_challenge_id: vars.challengeId,
        p_card_id: vars.cardId,
      });
      if (error) throw new Error('network');
      const r = data as { ok?: boolean; error?: string; card_id?: string };
      if (!r?.ok) throw new Error(r?.error ?? 'unknown');
      publishProgress('cards-granted');
      return r.card_id ?? vars.cardId;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: myLivePrizeChoicesKey(userId) });
      void qc.invalidateQueries({ queryKey: ['live-prize-choice-cards'] });
    },
  });
}
